import { TokenWithBalanceAndFormValue, TokenWithValue } from '@/domain/common/types'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { useConfettiContext } from '@/ui/molecules/confetti/Confetti'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'
import { useEffect } from 'react'
import { BridgeToken } from '../logic/useTrade'
import { SuccessViewCheckmark } from '@/features/dialogs/common/components/success-view/SuccessViewCheckmark'
import { L1Badge } from '@/ui/molecules/l1-badge/L1Badge'

export interface SuccessViewProps {
  runConfetti: boolean
  fromToken: BridgeToken | TokenWithBalanceAndFormValue
  toToken: BridgeToken | TokenWithBalanceAndFormValue
  resetForm: () => void
  isModal?: boolean
  stackedSuccessScreen?: boolean
}

export function SuccessView({
  fromToken,
  toToken,
  runConfetti,
  resetForm,
  isModal,
  stackedSuccessScreen,
}: SuccessViewProps) {
  const { runAnimation } = useConfettiContext()

  useEffect(() => {
    if (runConfetti) {
      runAnimation()
    }
  }, [runConfetti, runAnimation])

  return (
    <div className="flex w-full flex-col items-center justify-center">
      <SuccessViewCheckmark isModal={isModal} />
      <Panel className="mt-8 min-w-full max-w-3xl">
        <Panel.Header>
          <Panel.Title className="text-xl">Summary</Panel.Title>
        </Panel.Header>

        <Panel.Content>
          <div className={cn('mt-4 flex flex-col gap-9', { 'md:flex-row': !stackedSuccessScreen })}>
            <div className="flex grow flex-col" data-testid={testIds.easyBorrow.success.deposited}>
              <Typography variant="prompt" className="mb-2 font-semibold text-primary">
                Transferred
              </Typography>

              <Item
                key={fromToken.token.symbol}
                {...fromToken}
                isL1={(fromToken as BridgeToken).tokenId !== undefined}
              />
            </div>

            <div className="flex grow flex-col" data-testid={testIds.easyBorrow.success.borrowed}>
              <Typography variant="prompt" className="mb-2 font-semibold text-primary">
                Received
              </Typography>

              <Item key={toToken.token.symbol} {...toToken} isL1={(toToken as BridgeToken).tokenId !== undefined} />
            </div>
          </div>

          <div className="mt-8 flex gap-4">
            <Button className="flex-1 bg-accent text-primary" onClick={resetForm} rounded="full">
              Close
            </Button>
          </div>
        </Panel.Content>
      </Panel>
    </div>
  )
}

function Item({ token, value, approx = false, isL1 = false }: TokenWithValue & { approx?: boolean; isL1?: boolean }) {
  return (
    <div className="flex items-center border-t">
      <div className="relative">
        <TokenIcon token={token} className="h-6 w-6" />
        {isL1 && <L1Badge />}
      </div>

      <Typography className="ml-2 font-primary">{token.symbol}</Typography>

      <div className="flex grow flex-col py-3 pr-4">
        <Typography className="text-right font-primary">
          {approx && <span className="mr-1">~</span>}
          {token.format(value, { style: 'auto' })}
        </Typography>
        <Typography variant="prompt" className="text-right">
          {approx && <span className="mr-1">~</span>}
          {token.formatUSD(value)}
        </Typography>
      </div>
    </div>
  )
}
